import type { Router } from 'found';
import type { TFunction } from 'i18next';

import { externalLinks, routes } from '../../../constants';
import type { User } from '../../../types/User';
import type { IconName } from '../../../utils/getMuiIcon';

export const BANNER_TITLE = 'meinUnterricht 360';
export const DEFAULT_SUBTITLE = 'Library + Tools + Professional Development';

export enum UserUpgradeCategory {
	UPGRADE_ALLOWED = 'UPGRADE_ALLOWED',
	COURSE_USER_NO_UPGRADE = 'COURSE_USER_NO_UPGRADE',
	LIBRARY_USER = 'LIBRARY_USER',
}

export const PremiumBenefits = {
	features: [
		{
			text: '150.000+ teaching materials',
			isHighlighted: true,
		},
		{
			text: '200 downloads per month',
		},
		{
			text: 'Unlimited access to courses',
		},
		{
			text: 'AI-Assistant Muki',
		},
		{
			text: 'And much more',
		},
	],
};

export type AdditionalInfoType = 'premium' | 'course';
export interface BannerConfig {
	subtitle?: string;
	additionalInfoType: AdditionalInfoType;
	buttonText: string;
	buttonAction: () => void;
}

export interface UpgradeBannerProps {
	Image: React.ElementType;
	title: string;
	subtitle?: string;
	additionalInfoType: AdditionalInfoType;
	button: {
		text: string;
		onClick: () => void;
		icon: IconName;
	};
}

/**
 * Determines which banner scenario to display based on user properties
 * @param user - User object containing upgrade permissions and user type
 * @returns UserUpgradeCategory enum value
 */
export const getUserUpgradeCategory = (user: Partial<User>): UserUpgradeCategory => {
	const { canUpgrade, isCourseUser, isLibraryUser, isSchoolUser } = user;

	if (isLibraryUser || (isSchoolUser && isCourseUser)) return UserUpgradeCategory.LIBRARY_USER;
	if (canUpgrade) return UserUpgradeCategory.UPGRADE_ALLOWED;
	if (!canUpgrade && isCourseUser) return UserUpgradeCategory.COURSE_USER_NO_UPGRADE;

	return UserUpgradeCategory.UPGRADE_ALLOWED;
};

/**
 * Creates banner configuration based on user scenario
 * @param user - User object containing upgrade permissions and user type
 * @param router - Router object for navigation
 * @returns BannerConfig object with subtitle, additional info type, and button configuration
 */
export const createBannerConfig = (user: Partial<User>, router: Router): BannerConfig => {
	const userUpgradeCategory = getUserUpgradeCategory(user);

	switch (userUpgradeCategory) {
		case UserUpgradeCategory.UPGRADE_ALLOWED:
			return {
				subtitle: DEFAULT_SUBTITLE,
				additionalInfoType: 'premium',
				buttonText: 'Adjust plan',
				buttonAction: () => router.push(`/${routes.upgradePackageSelection.root}`),
			};

		case UserUpgradeCategory.COURSE_USER_NO_UPGRADE:
			return {
				additionalInfoType: 'course',
				buttonText: 'Contact support',
				buttonAction: () => window.open(externalLinks.faq, '_blank'),
			};

		case UserUpgradeCategory.LIBRARY_USER:
			return {
				subtitle: DEFAULT_SUBTITLE,
				additionalInfoType: 'premium',
				buttonText: 'Adjust plan',
				buttonAction: () => window.open(externalLinks.schoolLicenceOffer, '_blank'),
			};
	}
};

/**
 * Gets the complete banner props object for the upgrade banner
 * @param user - User object containing upgrade permissions and user type
 * @param router - Router object for navigation
 * @param UpgradeIcon - Icon component to display in the banner
 * @returns Complete banner props object ready to be passed to ReaderBanner component
 */
export const getUpgradeBannerProps = (
	user: User,
	router: Router,
	UpgradeIcon: React.ElementType,
	t: TFunction
): UpgradeBannerProps => {
	const config = createBannerConfig(user, router);

	return {
		Image: UpgradeIcon,
		title: t(BANNER_TITLE),
		subtitle: config.subtitle ? t(config.subtitle) : undefined,
		additionalInfoType: config.additionalInfoType,
		button: {
			text: t(config.buttonText),
			onClick: config.buttonAction,
			icon: 'starBanner' as const,
		},
	};
};
